import type { JSX } from 'react';

/**
 * Inline SVG icons for the modal grids. Stroke-style 24×24 — matches the
 * weight in the existing product's screenshots.
 *
 * They live together so adding a new tool is a one-icon-then-export change
 * rather than a sprawl of separate component files.
 */

const stroke = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export function DeviceIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <rect x="3" y="5" width="14" height="10" rx="2" />
      <rect x="14" y="9" width="7" height="11" rx="1.5" />
      <path d="M3 19h11" />
    </svg>
  );
}
export function VpnShieldIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <path d="M12 3 4 6v6c0 4 3 7 8 9 5-2 8-5 8-9V6l-8-3Z" />
    </svg>
  );
}
export function LogsIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </svg>
  );
}
export function MonitorIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <rect x="3" y="4" width="18" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}
export function ExpandIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <path d="m4 14 6 6M4 20h6v-6M20 10l-6-6M20 4h-6v6" />
    </svg>
  );
}
export function HandIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <path d="M9 11V5a1.5 1.5 0 0 1 3 0v5M12 10V4a1.5 1.5 0 0 1 3 0v6M15 10V6a1.5 1.5 0 0 1 3 0v8a6 6 0 0 1-6 6h-2.6a4 4 0 0 1-3.1-1.5L3 14a2 2 0 0 1 3-2.6L9 14" />
    </svg>
  );
}
export function RainIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <path d="M7 17a4.5 4.5 0 0 1 0-9 5.5 5.5 0 0 1 10.4 1A4 4 0 0 1 17 17H7Z" />
      <path d="M9 19v2M13 19v2M17 19v2" />
    </svg>
  );
}
export function MobiControlIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <rect x="6" y="2" width="12" height="20" rx="2" />
      <path d="M10 18h4" />
    </svg>
  );
}
export function SignalIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <path d="M4 18v-2M9 18v-5M14 18v-8M19 18V6" />
    </svg>
  );
}
export function NetworkIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <path d="M4 12h4M16 12h4M12 4v4M12 16v4" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
export function PinIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}
export function CalculatorIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M8 7h8M8 12h2M12 12h2M16 12h0M8 16h2M12 16h2M16 16h0" />
    </svg>
  );
}
export function ExitIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4M9 17l5-5-5-5M14 12H3" />
    </svg>
  );
}
export function RefreshIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <path d="M21 12a9 9 0 1 1-2.6-6.4M21 4v5h-5" />
    </svg>
  );
}
export function BugIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <rect x="7" y="8" width="10" height="11" rx="5" />
      <path d="M5 11h2M17 11h2M5 16h2M17 16h2M9 8a3 3 0 1 1 6 0M12 8v11" />
    </svg>
  );
}
export function EngineerIcon(): JSX.Element {
  return (
    <svg {...stroke} aria-hidden>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 21a7 7 0 0 1 14 0" />
      <circle cx="18" cy="6" r="2" />
      <path d="m17 4 1-1M19 4l-1-1" />
    </svg>
  );
}
export function SpeedIcon(): JSX.Element {
  // Speedometer dial — semicircular arc + needle pointing up-right.
  // Reads as "speed test" at the 24px action-grid card size.
  return (
    <svg {...stroke} aria-hidden>
      <path d="M3 17a9 9 0 1 1 18 0" />
      <path d="m12 17 5-6" />
      <circle cx="12" cy="17" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}
